import { ChangeDetectionStrategy, Component, Input, OnChanges } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { IActivityGroup } from "@org-common/lib/activities/activities-metadata";
import { ActivityPageService } from "@org-common/lib/activities/activity-page.service";
import { CulturalActivityMetadataByType, NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { LabellingService } from "@org-common/lib/labelling/labelling.service";
import { lastValueFrom } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { IObjectiveTeamGroup, ObjectivesService } from "../../objectives/objectives.service";

@Component({
    selector: "adapt-personal-objectives-activity-section",
    templateUrl: "./personal-objectives-activity-section.component.html",
    styleUrls: ["./personal-objectives-activity-section.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PersonalObjectivesActivitySectionComponent extends BaseComponent implements OnChanges {
    public readonly section = CulturalActivityMetadataByType[NonCulturalActivityType.Objectives];

    @Input({ required: true }) public activityGroup!: IActivityGroup<Objective>;
    @Input() public countTooltip?: string;
    @Input() public isLoading = false;
    @Input() public error?: any;
    @Input() public fallbackText?: string;

    public displayedCount = 0;

    public upcomingObjectiveGroups?: IObjectiveTeamGroup[];
    public overdueObjectiveGroups?: IObjectiveTeamGroup[];

    public constructor(
        private activitiesService: ActivitiesService,
        private objectivesService: ObjectivesService,
        private labellingService: LabellingService,
        activityPageService: ActivityPageService,
    ) {
        super();

        activityPageService.filterUpdated$.pipe(
            debounceTime(100),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.ngOnChanges());
    }

    public async ngOnChanges() {
        this.upcomingObjectiveGroups = this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Upcoming)
            ? this.objectivesService.groupObjectivesByTeam(this.activityGroup[CulturalActivityStatus.Upcoming])
                .map((group) => ({
                    team: group.team,
                    objectives: group.objectives.slice(0, 3),
                }))
            : [];
        this.overdueObjectiveGroups = this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Overdue)
            ? this.objectivesService.groupObjectivesByTeam(this.activityGroup[CulturalActivityStatus.Overdue])
                .map((group) => ({
                    team: group.team,
                    objectives: group.objectives.slice(0, 3),
                }))
            : [];

        const objectivesToPrime = this.upcomingObjectiveGroups
            .concat(this.overdueObjectiveGroups)
            .flatMap((g) => g.objectives);

        this.displayedCount = objectivesToPrime.length;

        await lastValueFrom(this.labellingService.primeLabelLocationsForObjectives(objectivesToPrime));
    }
}
